import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import FundDetailsSecondLevelTabGroup from 'components/Pages/FundsCenter/FundDetails/FundDetailsSecondLevelTabGroup';
import FundDetailsPortfolioSecuritiesTab from '../SecondLevelTabs/FundDetailsPortfolioSecuritiesTab';
import FundDetailsDerivativeTab from '../SecondLevelTabs/FundDetailsDerivativeTab';
import FundDetailsRiskAnalysisTab from '../SecondLevelTabs/FundDetailsRiskAnalysisTab';
import FundDetailsCodesTab from '../SecondLevelTabs/FundDetailsCodesTab';
import FundDetailsKeyInformationTab from '../SecondLevelTabs/FundDetailsKeyInformationTab';
import FundDetailsAuthorisedCountriesTab from '../SecondLevelTabs/FundDetailsAuthorisedCountriesTab';
import FundDetailsTradingInformationTab from '../SecondLevelTabs/FundDetailsTradingInformationTab';
import FundDetailsCorrelationMatrixTab from '../SecondLevelTabs/FundDetailsCorrelationMatrixTab';
import FundDetailsKeyRiskTab from '../SecondLevelTabs/FundDetailsKeyRiskTab';
import FundRiskStatisticsTab from '../SecondLevelTabs/FundRiskStatisticsTab';

import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import { useMemo } from 'react';
import { AvailableTabs } from '@/store/fundDetails/typings';
import { useSelector } from 'react-redux';
import { getAvailableTabs } from '@/store/fundDetails';
import { useFetchFundFactsTabDataQuery } from '@/store/fundDetails/services/fundDetailsApi';
import usePayload from 'hooks/useFundDetailsRequestPayload';

type Props = {
  title: string;
};

const FundDetailsFundsFactsTab = ({ title }: Props) => {
  const { t } = useI18n();
  const { isExperienceEditor, getErrorDictionary } = useHelpers();
  const availableTabs: AvailableTabs | null = useSelector(getAvailableTabs);
  const payload = usePayload();

  const { data, error, isLoading } = useFetchFundFactsTabDataQuery(payload, {
    skip: isExperienceEditor,
  });

  const tabComponentsByTabKeys = useMemo(() => {
    return {
      top: {
        portfolioSecuritiesTab: FundDetailsPortfolioSecuritiesTab,
        derivativeUsageTab: FundDetailsDerivativeTab,
        breakdownRiskTab: FundDetailsRiskAnalysisTab,
        codesTab: FundDetailsCodesTab,
        keyInformationTab: FundDetailsKeyInformationTab,
        authorizedCountriesTab: FundDetailsAuthorisedCountriesTab,
        tradingInformationTab: FundDetailsTradingInformationTab,
        correlationMatrixTab: FundDetailsCorrelationMatrixTab,
      },
      bottom: {
        keyRiskTab: FundDetailsKeyRiskTab,
        fundRiskStatisticsTab: FundRiskStatisticsTab,
      },
    };
  }, []);

  const tabTitlesByTabKeys = useMemo(() => {
    return {
      top: {
        portfolioSecuritiesTab: t('fundDetailsTabsPortfolioSecurities'),
        derivativeUsageTab: t('fundDetailsTabsDerivativeUsage'),
        breakdownRiskTab: t('fundDetailsTabsRiskAnalysis'),
        codesTab: t('fundDetailsTabsCodes'),
        keyInformationTab: t('fundDetailsTabsKeyInformation'),
        authorizedCountriesTab: t('fundDetailsTabsAuthorisedCountries'),
        tradingInformationTab: t('fundDetailsTabsTradingInformation'),
        correlationMatrixTab: t('fundDetailsTabsCorrelationMatrix'),
      },
      bottom: {
        keyRiskTab: t('fundDetailsTabsKeyRisk'),
        fundRiskStatisticsTab: t('fundDetailsTabsRiskStatistics'),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableFundFactsTabs = useMemo(() => {
    return {
      top: availableTabs?.fundFactsTab?.topTabs,
      bottom: availableTabs?.fundFactsTab?.bottomTabs,
    };
  }, [availableTabs]);

  return (
    <section className="default-section-padding background-white">
      <FundDetailsTabRow>
        <h2>{title}</h2>
        {isLoading && <FundDetailsTabSkeleton />}
        {error && (
          <AlertMessageBase type="error" title={t(getErrorDictionary(error))} className="mt-5" />
        )}
        {!isLoading && !error && data && data.length !== 0 && (
          <div className="mt-5">
            <h5>{t('fundDetailsTabsFundObjectives')}</h5>
            <div className="mt-2">
              {data.content.fundObjectives.map((item: string, index: number) => (
                <p key={index} className={`body-default text-grey ${index > 0 ? 'mt-3' : ''}`}>
                  {item}
                </p>
              ))}
            </div>
          </div>
        )}
      </FundDetailsTabRow>

      <FundDetailsSecondLevelTabGroup
        className="mt-8"
        availableTabs={availableFundFactsTabs.top}
        tabLvl="subTab"
        tabComponentsByTabKeys={tabComponentsByTabKeys.top}
        tabTitlesByTabKeys={tabTitlesByTabKeys.top}
      />

      <FundDetailsSecondLevelTabGroup
        className="mt-6"
        availableTabs={availableFundFactsTabs.bottom}
        tabLvl="subTab2"
        tabComponentsByTabKeys={tabComponentsByTabKeys.bottom}
        tabTitlesByTabKeys={tabTitlesByTabKeys.bottom}
      />
    </section>
  );
};

export default FundDetailsFundsFactsTab;
